import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, MoveDown } from "lucide-react";
import { scrollToId } from "@/components/Reveal";

const GoldField = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let w, h, raf;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pts = Array.from({ length: 46 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0006,
      vy: (Math.random() - 0.5) * 0.0006,
    }));

    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of pts) {
        p.x = (p.x + p.vx + 1) % 1;
        p.y = (p.y + p.vy + 1) % 1;
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = (pts[i].x - pts[j].x) * w;
          const dy = (pts[i].y - pts[j].y) * h;
          const dist = Math.hypot(dx, dy);
          if (dist < 150) {
            ctx.strokeStyle = `rgba(212,175,55,${0.10 * (1 - dist / 150)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(pts[i].x * w, pts[i].y * h);
            ctx.lineTo(pts[j].x * w, pts[j].y * h);
            ctx.stroke();
          }
        }
        ctx.fillStyle = "rgba(212,175,55,0.5)";
        ctx.beginPath();
        ctx.arc(pts[i].x * w, pts[i].y * h, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full opacity-70" aria-hidden="true" />;
};

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-obsidian"
    >
      <motion.div style={{ y: yGlow }} className="absolute inset-0" aria-hidden="true">
        <div className="absolute -left-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-[#2E1065]/50 blur-[140px]" />
        <div className="absolute -right-32 bottom-0 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-[160px]" />
      </motion.div>
      <GoldField />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-obsidian to-transparent" aria-hidden="true" />

      <motion.div
        style={{ y: yText, opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-10 flex flex-wrap items-center gap-4"
        >
          <span className="eyebrow" data-testid="hero-eyebrow">Vision Aries Financial Impact</span>
          <span className="hidden h-px w-16 bg-gold/40 sm:block" />
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
            <BadgeCheck size={13} className="text-gold" /> Licensed Strategist · GFI Affiliated
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          <Line delay={0.35}>Wealth is not luck.</Line>
          <Line delay={0.5}>
            It is <span className="text-gold-gradient">architecture.</span>
          </Line>
          <Line delay={0.65}>Build your legacy.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg"
          data-testid="hero-subcopy"
        >
          Life insurance, tax preparation, retirement engineering, and estate protection —
          designed with precision by a licensed strategist who treats your future like family.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            onClick={() => scrollToId("#contact")}
            data-testid="hero-consultation-button"
            className="group flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
          >
            Book a Wealth Consultation
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("#services")}
            data-testid="hero-services-button"
            className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white/80 transition-all duration-300 hover:border-gold/60 hover:text-gold"
          >
            Explore Tax Services
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-20 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/35"
          data-testid="hero-scroll-hint"
        >
          <MoveDown size={14} className="animate-pulse-gold text-gold" /> Scroll to begin
        </motion.div>
      </motion.div>
    </section>
  );
};
